import { useEffect, useState } from 'react'
import { api, type FindingsReport, type FindingsScope } from './api.ts'

/**
 * The findings report for one scope, asked again when the scope changes -- by
 * identity, so the caller keeps it stable between renders. The previous report
 * stays up while the next one loads, so changing a filter does not blank the
 * page.
 *
 * `answered.scope` is the scope the report on screen was counted under, which
 * is not the one being asked for while a new one loads: a list opened from a
 * number has to be about the report that number is in.
 */
export function useFindingsReport(scope: FindingsScope): {
  answered: { report: FindingsReport; scope: FindingsScope } | undefined
  loading: boolean
  error: string | undefined
} {
  const [answered, setAnswered] = useState<
    { report: FindingsReport; scope: FindingsScope } | undefined
  >()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    let current = true
    setLoading(true)
    setError(undefined)
    api
      .findingsReport(scope)
      .then((report) => {
        if (current) setAnswered({ report, scope })
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : 'falhou')
      })
      .finally(() => {
        if (current) setLoading(false)
      })
    return () => {
      current = false
    }
  }, [scope])

  return { answered, loading, error }
}
